import React, { useState, useEffect } from 'react';
import api from '../api';
import { FaSearch, FaPlus, FaEdit, FaTrash, FaEye, FaCommentDots, FaUsers, FaTimes, FaShoppingCart } from 'react-icons/fa';
import TelefoneInput from './TelefoneInput';
import PageLayout from './PageLayout';
import { theme, t } from '../theme';
import { useConfirm } from './ConfirmProvider';
import { formatarTelefone } from '../utils';
import useViewMode from '../hooks/useViewMode';
import ViewToggle from './ViewToggle';

const tableActionBtn = (color, bg) => ({
  width: 32, height: 32, borderRadius: 8, border: 'none',
  background: bg, color, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
});

const Clientes = () => {
  const [clientes, setClientes] = useState([]);
  const [stats, setStats] = useState({});
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [clienteEditando, setClienteEditando] = useState(null);
  const [clienteDetalhe, setClienteDetalhe] = useState(null);
  const confirm = useConfirm();
  const { mode, setMode, effective } = useViewMode('view:clientes');

  useEffect(() => { carregarDados(); }, []);
  const queroAbrirNovoRef = React.useRef(
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('novo') === '1'
  );
  useEffect(() => {
    if (loading) return;
    if (!queroAbrirNovoRef.current) return;
    queroAbrirNovoRef.current = false;
    setClienteEditando(null);
    setModalAberto(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') carregarDados(); };
    window.addEventListener('focus', onVis);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', onVis);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregarDados = async () => {
    setLoading(true);
    // Faz as duas chamadas independentes — falha em uma não bloqueia a outra
    try {
      const resClientes = await api.get('/api/clientes');
      const lista = Array.isArray(resClientes.data) ? resClientes.data : (resClientes.data?.clientes || []);
      setClientes(lista);
    } catch (e) { console.error('[Clientes] erro listar:', e); }
    try {
      const resStats = await api.get('/api/clientes/stats');
      setStats(resStats.data || {});
    } catch (e) { console.error('[Clientes] erro stats:', e); }
    setLoading(false);
  };

  const buscar = async (termo) => {
    setBusca(termo);
    try {
      const res = await api.get(`/api/clientes?busca=${encodeURIComponent(termo)}`);
      setClientes(res.data);
    } catch {}
  };

  const handleDelete = async (id, nome) => {
    if (!await confirm(`Remover cliente "${nome}"?`, { danger: true, confirmText: 'Excluir' })) return;
    setClientes(prev => prev.filter(c => c.id !== id));
    try { await api.delete(`/api/clientes/${id}`); } catch { carregarDados(); }
  };

  const abrirWhatsApp = (telefone) => {
    const num = telefone.replace(/\D/g, '');
    window.open(`https://wa.me/${num}`, '_blank');
  };

  const abrirDetalhe = async (id) => {
    try {
      const res = await api.get(`/api/clientes/${id}`);
      setClienteDetalhe(res.data);
    } catch {}
  };

  const formatarMoeda = (v) => `R$ ${(v || 0).toFixed(2).replace('.', ',')}`;

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>Carregando...</div>;

  return (
    <PageLayout title="Clientes" subtitle="Gerencie sua base de clientes"
      action={<button onClick={() => { setClienteEditando(null); setModalAberto(true); }} style={s.btnPrimary}><FaPlus /> Novo Cliente</button>}>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard icon={<FaUsers />} valor={stats.total_clientes || 0} label="Clientes" cor={theme.colors.primary} corLight={theme.colors.primaryLight} />
        <StatCard icon={<FaShoppingCart />} valor={stats.total_pedidos || 0} label="Pedidos" cor={theme.colors.info} corLight={theme.colors.infoLight} />
        <StatCard icon={<FaCommentDots />} valor={formatarMoeda(stats.receita_total)} label="Receita Total" cor={theme.colors.success} corLight={theme.colors.successLight} isText />
        <StatCard icon={<FaUsers />} valor={stats.aniversarios || 0} label="Aniversários do mês" cor={theme.colors.warning} corLight={theme.colors.warningLight} />
      </div>

      {/* Busca */}
      <div style={{ background: '#faf7f5', borderRadius: 16, padding: 20, marginBottom: 24 }}>
        <div style={{ position: 'relative' }}>
          <FaSearch style={{ position: 'absolute', left: 16, top: 14, color: '#bbb' }} />
          <input
            placeholder="Pesquisar por nome ou telefone..."
            value={busca}
            onChange={e => buscar(e.target.value)}
            style={{ ...s.input, paddingLeft: 44, fontSize: 15, padding: '12px 16px 12px 44px', borderRadius: 12, border: 'none', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
          />
        </div>
      </div>

      {/* Lista */}
      <div style={{ background: '#faf7f5', borderRadius: 16, padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaUsers style={{ color: '#bbb' }} /> Lista de Clientes ({clientes.length})
          </h3>
          <ViewToggle mode={mode} setMode={setMode} />
        </div>

        {clientes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#ccc' }}>
            <FaUsers size={40} style={{ marginBottom: 12 }} />
            <p>Nenhum cliente encontrado.</p>
          </div>
        ) : effective === 'cards' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {clientes.map(c => (
              <ClienteCard key={c.id} cliente={c}
                onWhats={abrirWhatsApp}
                onView={abrirDetalhe}
                onEdit={(cl) => { setClienteEditando(cl); setModalAberto(true); }}
                onDelete={handleDelete}
                formatarMoeda={formatarMoeda}
                formatarTelefone={formatarTelefone}
              />
            ))}
          </div>
        ) : (
          <div className="table-responsive" style={{ background: '#fff', borderRadius: 12, padding: 8, border: '1px solid #f0f0f0', overflowX: 'auto' }}>
            <table className="table table-sm align-middle" style={{ marginBottom: 0, width: '100%' }}>
              <thead style={{ background: '#faf7f5' }}>
                <tr>
                  <th style={{ fontSize: 11, textTransform: 'uppercase', color: '#888', fontWeight: 600, letterSpacing: '0.04em' }}>Nome</th>
                  <th style={{ fontSize: 11, textTransform: 'uppercase', color: '#888', fontWeight: 600, letterSpacing: '0.04em' }}>Telefone</th>
                  <th className="text-center" style={{ fontSize: 11, textTransform: 'uppercase', color: '#888', fontWeight: 600, letterSpacing: '0.04em' }}>Pedidos</th>
                  <th className="text-end" style={{ fontSize: 11, textTransform: 'uppercase', color: '#888', fontWeight: 600, letterSpacing: '0.04em' }}>Total Gasto</th>
                  <th className="text-center" style={{ fontSize: 11, textTransform: 'uppercase', color: '#888', fontWeight: 600, letterSpacing: '0.04em' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {clientes.map(c => (
                  <tr key={c.id}>
                    <td><strong>{c.nome}</strong></td>
                    <td>{c.telefone ? formatarTelefone(c.telefone) : '-'}</td>
                    <td className="text-center">
                      <span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '2px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                        {c.total_pedidos || 0}
                      </span>
                    </td>
                    <td className="text-end" style={{ fontWeight: 600 }}>{formatarMoeda(c.total_gasto)}</td>
                    <td className="text-center">
                      <div style={{ display: 'inline-flex', gap: 4 }}>
                        {c.telefone && <button onClick={() => abrirWhatsApp(c.telefone)} title="WhatsApp" style={tableActionBtn('#25d366', '#e8f5e9')}><FaCommentDots size={14} /></button>}
                        <button onClick={() => abrirDetalhe(c.id)} title="Ver" style={tableActionBtn('#666', '#f1f3f5')}><FaEye size={14} /></button>
                        <button onClick={() => { setClienteEditando(c); setModalAberto(true); }} title="Editar" style={tableActionBtn('#555', '#f1f3f5')}><FaEdit size={14} /></button>
                        <button onClick={() => handleDelete(c.id, c.nome)} title="Remover" style={tableActionBtn('#e74c3c', '#fdecea')}><FaTrash size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Criar/Editar */}
      {modalAberto && (
        <ClienteModal
          cliente={clienteEditando}
          onClose={() => setModalAberto(false)}
          onSave={(data, isEdit) => {
            setModalAberto(false);
            if (isEdit) setClientes(prev => prev.map(c => c.id === data.id ? data : c));
            else setClientes(prev => [data, ...prev]);
          }}
        />
      )}

      {/* Modal Detalhe */}
      {clienteDetalhe && (
        <ClienteDetalheModal
          cliente={clienteDetalhe}
          onClose={() => setClienteDetalhe(null)}
          onRefresh={() => abrirDetalhe(clienteDetalhe.id)}
        />
      )}
    </PageLayout>
  );
};

// ---------------------------------------------------------------------------
// Cliente Card (mobile-first card layout)
// ---------------------------------------------------------------------------
function ClienteCard({ cliente, onWhats, onView, onEdit, onDelete, formatarMoeda, formatarTelefone }) {
  const iniciais = (cliente.nome || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join('');
  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #eee', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ height: 4, background: theme.colors.primary }} />
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: theme.colors.primaryLight, color: theme.colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
            {iniciais}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 600, color: '#2c3e50', fontSize: 15, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cliente.nome}</div>
            {cliente.telefone && (
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>📞 {formatarTelefone(cliente.telefone)}</div>
            )}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#aaa', fontWeight: 600, marginBottom: 2 }}>Pedidos</div>
            <span style={{ display: 'inline-block', background: '#e8f5e9', color: '#2e7d32', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>{cliente.total_pedidos || 0}</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#aaa', fontWeight: 600, marginBottom: 2 }}>Total gasto</div>
            <div style={{ color: '#444', fontSize: 13, fontWeight: 600 }}>{formatarMoeda(cliente.total_gasto)}</div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
          {cliente.telefone && (
            <button onClick={() => onWhats(cliente.telefone)} title="WhatsApp"
              style={{ width: 36, height: 36, borderRadius: 10, border: 'none', background: '#e8f5e9', color: '#25d366', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaCommentDots size={17} /></button>
          )}
          <button onClick={() => onView(cliente.id)} title="Ver detalhes"
            style={{ width: 36, height: 36, borderRadius: 10, border: 'none', background: '#f1f3f5', color: '#666', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaEye size={17} /></button>
          <button onClick={() => onEdit(cliente)} title="Editar"
            style={{ width: 36, height: 36, borderRadius: 10, border: 'none', background: '#f1f3f5', color: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaEdit size={17} /></button>
          <button onClick={() => onDelete(cliente.id, cliente.nome)} title="Remover"
            style={{ width: 36, height: 36, borderRadius: 10, border: 'none', background: '#fdecea', color: '#e74c3c', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaTrash size={17} /></button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat Card
// ---------------------------------------------------------------------------
const StatCard = ({ icon, valor, label, cor, corLight, isText }) => (
  <div className="kpi-card" style={{ ...t.card, padding: '20px 24px' }}>
    <div className="kpi-card-head" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      <div className="kpi-card-icon" style={{ width: 36, height: 36, borderRadius: 10, background: corLight || theme.colors.bgSection, display: 'flex', alignItems: 'center', justifyContent: 'center', color: cor, fontSize: 16 }}>
        {icon}
      </div>
      <span className="kpi-card-label" style={{ fontSize: 13, fontWeight: 600, color: theme.colors.textSecondary }}>{label}</span>
    </div>
    <div className="kpi-card-value" style={{ fontSize: isText ? 20 : 26, fontWeight: 700, color: theme.colors.text, letterSpacing: '-0.02em' }}>{valor}</div>
  </div>
);

// ---------------------------------------------------------------------------
// Modal Criar/Editar
// ---------------------------------------------------------------------------
const ClienteModal = ({ cliente, onClose, onSave }) => {
  const [nome, setNome] = useState(cliente?.nome || '');
  const [telefone, setTelefone] = useState(cliente?.telefone || '');
  const [dataNascimento, setDataNascimento] = useState(cliente?.data_nascimento || '');
  const [endereco, setEndereco] = useState(cliente?.endereco || '');
  const [observacoes, setObservacoes] = useState(cliente?.observacoes || '');
  const [erro, setErro] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro(null);
    const payload = { nome, telefone, data_nascimento: dataNascimento || null, endereco, observacoes };
    try {
      let res;
      if (cliente?.id) { res = await api.put(`/api/clientes/${cliente.id}`, payload); }
      else { res = await api.post('/api/clientes', payload); }
      onSave(res.data, !!cliente?.id);
    } catch (err) { setErro(err.response?.data?.erro || 'Erro ao salvar.'); }
  };

  return (
    <div style={s.overlay} className="modal-overlay-mobile">
      <div style={s.modal} className="modal-content-sheet">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{cliente ? 'Editar Cliente' : 'Novo Cliente'}</h3>
            <p style={{ margin: '4px 0 0', color: '#999', fontSize: 13 }}>
              {cliente ? 'Atualize os dados do cliente' : 'Cadastre um novo cliente'}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', fontSize: 20 }}><FaTimes /></button>
        </div>
        {erro && <div style={s.error}>{erro}</div>}
        <form onSubmit={handleSubmit}>
          <div style={s.field}>
            <label style={s.label}>Nome *</label>
            <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: Maria Silva" style={s.inputModal} required />
          </div>
          <div style={s.field}>
            <label style={s.label}>Telefone / WhatsApp *</label>
            <TelefoneInput value={telefone} onChange={setTelefone} style={s.inputModal} required />
          </div>
          <div style={s.field}>
            <label style={s.label}>Data de Nascimento</label>
            <input type="date" value={dataNascimento} onChange={e => setDataNascimento(e.target.value)} style={s.inputModal} />
          </div>
          <div style={s.field}>
            <label style={s.label}>Endereço</label>
            <textarea value={endereco} onChange={e => setEndereco(e.target.value)} placeholder="Endereço completo" style={{ ...s.inputModal, minHeight: 70, resize: 'vertical' }} />
          </div>
          <div style={s.field}>
            <label style={s.label}>Observações</label>
            <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)} placeholder="Preferências, alergias, etc." style={{ ...s.inputModal, minHeight: 70, resize: 'vertical' }} />
          </div>
          <button type="submit" style={{ ...s.btnPrimary, width: '100%', justifyContent: 'center', padding: '12px 0', fontSize: 15, borderRadius: 12, marginTop: 8 }}>
            {cliente ? 'Salvar Alterações' : 'Adicionar Cliente'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Modal Detalhe
// ---------------------------------------------------------------------------
const ClienteDetalheModal = ({ cliente, onClose, onRefresh }) => {
  const confirm = useConfirm();
  const [mostrarNovoPedido, setMostrarNovoPedido] = useState(false);
  const [descricao, setDescricao] = useState('');
  const [valorTotal, setValorTotal] = useState('');
  const [dataEntrega, setDataEntrega] = useState('');

  const criarPedido = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/clientes/${cliente.id}/pedidos`, {
        descricao, valor_total: parseFloat(valorTotal.replace(',', '.')) || 0, data_entrega: dataEntrega || null,
      });
      setMostrarNovoPedido(false);
      setDescricao(''); setValorTotal(''); setDataEntrega('');
      onRefresh();
    } catch {}
  };

  const deletarPedido = async (id) => {
    if (!await confirm('Remover pedido?', { danger: true, confirmText: 'Excluir' })) return;
    try { await api.delete(`/api/pedidos/${id}`); onRefresh(); } catch {}
  };

  const formatarMoeda = (v) => `R$ ${(v || 0).toFixed(2).replace('.', ',')}`;
  const formatarData = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '-';

  const statusCores = { confirmado: '#27ae60', em_producao: '#f39c12', pronto: '#3498db', entregue: '#7f8c8d', cancelado: '#e74c3c', pendente: '#27ae60' };

  return (
    <div style={s.overlay} className="modal-overlay-mobile">
      <div style={{ ...s.modal, maxWidth: 560 }} className="modal-content-sheet">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{cliente.nome}</h3>
            <p style={{ margin: '4px 0 0', color: '#999', fontSize: 13 }}>📞 {formatarTelefone(cliente.telefone)}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', fontSize: 20 }}><FaTimes /></button>
        </div>

        {cliente.endereco && <p style={{ fontSize: 13, color: '#666', margin: '0 0 4px' }}>📍 {cliente.endereco}</p>}
        {cliente.data_nascimento && <p style={{ fontSize: 13, color: '#666', margin: '0 0 4px' }}>🎂 {formatarData(cliente.data_nascimento)}</p>}
        {cliente.observacoes && <p style={{ fontSize: 13, color: '#888', fontStyle: 'italic', margin: '0 0 12px' }}>💬 {cliente.observacoes}</p>}

        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, background: '#f0f9ff', borderRadius: 10, padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#2c3e50' }}>{cliente.total_pedidos}</div>
            <div style={{ fontSize: 12, color: '#888' }}>Pedidos</div>
          </div>
          <div style={{ flex: 1, background: '#f0fdf4', borderRadius: 10, padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#27ae60' }}>{formatarMoeda(cliente.total_gasto)}</div>
            <div style={{ fontSize: 12, color: '#888' }}>Total Gasto</div>
          </div>
        </div>

        {/* Pedidos */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#555' }}>Pedidos</h4>
          <button onClick={() => setMostrarNovoPedido(!mostrarNovoPedido)} style={{ ...s.btnSmall, background: '#d4807c' }}>
            <FaPlus style={{ fontSize: 10 }} /> Novo Pedido
          </button>
        </div>

        {mostrarNovoPedido && (
          <form onSubmit={criarPedido} style={{ background: '#faf7f5', borderRadius: 10, padding: 14, marginBottom: 12 }}>
            <div style={s.field}><label style={s.label}>Descrição</label><input value={descricao} onChange={e => setDescricao(e.target.value)} style={s.inputModal} placeholder="Ex: Bolo 20cm chocolate" /></div>
            <div className="form-grid-2">
              <div style={s.field}><label style={s.label}>Valor (R$)</label><input value={valorTotal} onChange={e => setValorTotal(e.target.value)} style={s.inputModal} placeholder="0,00" /></div>
              <div style={s.field}><label style={s.label}>Entrega</label><input type="date" value={dataEntrega} onChange={e => setDataEntrega(e.target.value)} style={s.inputModal} /></div>
            </div>
            <button type="submit" style={{ ...s.btnSmall, background: '#27ae60' }}>Salvar Pedido</button>
          </form>
        )}

        {(cliente.pedidos || []).length === 0 ? (
          <p style={{ textAlign: 'center', color: '#ccc', padding: 20, fontSize: 13 }}>Nenhum pedido registrado.</p>
        ) : (
          <div style={{ maxHeight: 250, overflowY: 'auto' }}>
            {(cliente.pedidos || []).map(p => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Pedido #{p.id}{p.descricao ? ` — ${p.descricao}` : ''}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    {formatarData(p.data_entrega)} &middot;
                    <span style={{ color: statusCores[p.status] || '#888', fontWeight: 600, marginLeft: 4 }}>{p.status}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600, color: '#27ae60' }}>{formatarMoeda(p.valor_total)}</span>
                  <button onClick={() => deletarPedido(p.id)} style={s.btnIcon} title="Remover"><FaTrash style={{ color: '#e74c3c', fontSize: 13 }} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const s = {
  th: t.th, td: t.td,
  btnPrimary: t.btnPrimary,
  btnSmall: t.btnSmall,
  btnIcon: t.btnIcon,
  input: t.input,
  inputModal: t.input,
  overlay: t.overlay,
  modal: { ...t.modal, width: 480 },
  field: { marginBottom: 16 },
  label: t.label,
  error: t.error,
  ownerBadge: { background: theme.colors.primaryLight, color: theme.colors.primary, fontSize: 11, padding: '2px 8px', borderRadius: 10, marginLeft: 8, fontWeight: 600 },
  removeBtn: { background: 'none', border: 'none', color: theme.colors.danger, fontSize: 13, cursor: 'pointer' },
};

export default Clientes;
